// Finding an employer in the FMCSA registry.
//
// A DOT number and a company name are different lookups, so what was typed
// decides which one runs — digits, optionally after "DOT" or "USDOT", are a DOT
// number; anything else is a name. The name search is forgiving on the server
// (spellings, aliases, the carrier directory), so the text goes up as typed.
// Both are debounced, and the query only fires once there is enough to search
// for; a single character matches half the registry and costs a round trip to
// learn nothing.

import { useEffect, useMemo, useState } from 'react'

import { useLazyQuery } from '@apollo/client'
import { useDebouncedValue } from '@mantine/hooks'

import {
  SEARCH_FMCSA_COMPANY_BY_DOT_NUMBER,
  SEARCH_FMCSA_COMPANY_BY_NAME,
} from 'src/graphql/fmcsa.graphql'

import type { CarrierOptionCompany } from './carrierOptionDetails'
import { parseDotNumberQuery } from './dotNumberQuery'

/** Below this a search matches too much to be worth the round trip. */
export const MIN_QUERY_LENGTH = 2
const DEBOUNCE_MS = 400

export type FmcsaSearchCompany = CarrierOptionCompany & {
  phone?: string | null
  inDirectory?: boolean | null
}

export interface CarrierSearchState {
  results: FmcsaSearchCompany[]
  searching: boolean
  /** The registry did not answer. Different from it answering with nothing. */
  failed: boolean
  /** True once a query has actually been run for the current text. */
  searched: boolean
  /**
   * The name search answered empty without saying how it read the query: the
   * shape of the server's rate limit, not of a carrier that does not exist.
   */
  throttled: boolean
  /** The query as the server searched it, for telemetry. Null until known. */
  normalizedQuery: string | null
  /** The query was read as a DOT number and looked up by it. */
  dotLookup: boolean
}

export const useCarrierSearch = (query: string): CarrierSearchState => {
  const trimmed = query.trim()
  const [debounced] = useDebouncedValue(trimmed, DEBOUNCE_MS)
  const [searched, setSearched] = useState(false)

  const dotNumber = parseDotNumberQuery(debounced)
  const isDotNumber = dotNumber !== null
  const longEnough = debounced.length >= MIN_QUERY_LENGTH

  const [searchByName, byName] = useLazyQuery(SEARCH_FMCSA_COMPANY_BY_NAME)
  const [searchByDot, byDot] = useLazyQuery(SEARCH_FMCSA_COMPANY_BY_DOT_NUMBER)

  useEffect(() => {
    if (!longEnough) {
      setSearched(false)
      return
    }
    setSearched(true)
    if (isDotNumber) {
      void searchByDot({ variables: { dotNumber } })
    } else {
      void searchByName({ variables: { name: debounced } })
    }
  }, [debounced, dotNumber, isDotNumber, longEnough, searchByDot, searchByName])

  const active = isDotNumber ? byDot : byName
  // Only the response for the text being searched now. Two lookups can be in
  // flight — a name and a DOT number, or two names — and they can finish out
  // of order, which put an older list under the user's finger and let them
  // pick an employer they never searched for.
  const answerIsCurrent = isDotNumber
    ? byDot.variables?.dotNumber === dotNumber
    : byName.variables?.name === debounced

  const results = useMemo(() => {
    if (!longEnough || !answerIsCurrent) return []
    if (isDotNumber) {
      const company = byDot.data?.searchResult?.company
      return company ? [company] : []
    }
    const companies: FmcsaSearchCompany[] =
      byName.data?.searchResults?.companies ?? []
    return companies.filter((company) => company.companyName)
  }, [answerIsCurrent, byDot.data, byName.data, isDotNumber, longEnough])

  const nameAnswer =
    !isDotNumber && answerIsCurrent ? byName.data?.searchResults : undefined

  return {
    results,
    // Typing counts as searching, and so does holding a response that belongs
    // to an earlier query — either way the list on screen is not the answer yet
    searching:
      longEnough &&
      (active.loading || debounced !== trimmed || !answerIsCurrent),
    failed: Boolean(active.error),
    searched: searched && longEnough,
    throttled: Boolean(
      nameAnswer &&
        !active.loading &&
        !active.error &&
        (nameAnswer.companies ?? []).length === 0 &&
        nameAnswer.normalizedQuery == null
    ),
    normalizedQuery: isDotNumber
      ? dotNumber
      : nameAnswer?.normalizedQuery ?? null,
    dotLookup: longEnough && isDotNumber,
  }
}
